/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BioIndexExploreNavigation from "./BioIndexExploreNavigation";
import TaxonomyStepper from "./TaxonomyStepper";
import ExplorePhotoModes from "./ExplorePhotoModes";
import { defaultImg } from "../utils/icons";
import "../styles/BioIndexCommunityMobile.css";
import "../styles/BioIndexExplore.css";

export function BioIndexTaxonCard({
  name,
  image,
  count,
  to,
  italic = false,
  level,
}) {
  return (
    <Link
      to={to}
      className={`bioindex-explore-kingdom-card bioindex-explore-${level}-card`}
      aria-label={`${name}, ${count} species`}
    >
      <span
        className="bioindex-explore-kingdom-count"
        title={`${count.toLocaleString()} species`}
      >
        {count.toLocaleString()}
      </span>
      <div className="bioindex-explore-kingdom-illustration">
        <img
          src={image || defaultImg}
          alt=""
          loading="lazy"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = defaultImg;
          }}
        />
      </div>
      <div className="bioindex-explore-kingdom-caption">
        <h2 className={italic ? "italic" : ""}>{name}</h2>
      </div>
    </Link>
  );
}

export default function BioIndexExploreLevelLayout({
  uid,
  dataUID,
  currentLevel,
  params,
  browseMode,
  setBrowseMode,
  isOwnCollection,
  itemName,
  itemCount,
  secondaryCount,
  secondaryUnit = "species",
  adminModeActive,
  topContent = null,
  modalOpen = false,
  children,
}) {
  const stepperPanelRef = useRef(null);
  const [stepperScroll, setStepperScroll] = useState({
    canScroll: false,
    thumbWidth: 72,
    thumbOffset: 0,
  });

  useEffect(() => {
    const viewport = stepperPanelRef.current?.querySelector(".overflow-x-auto");
    if (!viewport) return undefined;

    const trackWidth = 72;
    function updateScrollIndicator() {
      const visibleWidth = viewport.clientWidth;
      const fullWidth = viewport.scrollWidth;
      const maxScroll = Math.max(0, fullWidth - visibleWidth);
      const canScroll = maxScroll > 2;
      const thumbWidth = canScroll
        ? Math.max(
            20,
            Math.min(trackWidth, (visibleWidth / fullWidth) * trackWidth),
          )
        : trackWidth;
      const scrollFraction = canScroll
        ? Math.min(1, Math.max(0, viewport.scrollLeft / maxScroll))
        : 0;
      const thumbOffset = scrollFraction * (trackWidth - thumbWidth);
      setStepperScroll((previous) => {
        if (
          previous.canScroll === canScroll &&
          Math.abs(previous.thumbWidth - thumbWidth) < 0.5 &&
          Math.abs(previous.thumbOffset - thumbOffset) < 0.5
        ) {
          return previous;
        }
        return { canScroll, thumbWidth, thumbOffset };
      });
    }

    viewport.addEventListener("scroll", updateScrollIndicator, {
      passive: true,
    });
    window.addEventListener("resize", updateScrollIndicator);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollIndicator)
        : null;
    resizeObserver?.observe(viewport);
    if (viewport.firstElementChild) {
      resizeObserver?.observe(viewport.firstElementChild);
    }
    updateScrollIndicator();

    return () => {
      viewport.removeEventListener("scroll", updateScrollIndicator);
      window.removeEventListener("resize", updateScrollIndicator);
      resizeObserver?.disconnect();
    };
  }, []);

  const currentModeTitle = {
    taxonomy:
      itemName === "species"
        ? "Species"
        : itemName.charAt(0).toUpperCase() + itemName.slice(1),
    alpha: "Alphabetical browsing",
    date: "Observations by date",
    location: "Observations by location",
  }[browseMode];

  return (
    <div
      className={`bioindex-explore-page bioindex-explore-${currentLevel}-page`}
    >
      <div className={modalOpen ? "bioindex-explore-modal-nav-underlay" : ""}>
        <BioIndexExploreNavigation uid={uid} />
      </div>

      <main className="bioindex-explore-main">
        <header className="bioindex-explore-intro">
          <div className="bioindex-explore-intro-text">
            <span className="bioindex-explore-eyebrow">
              {isOwnCollection ? "Your collection" : "Visiting a collection"}
            </span>
            <h1>{currentModeTitle}</h1>
          </div>
        </header>

        <section
          ref={stepperPanelRef}
          className="bioindex-explore-stepper-panel"
          aria-label="Taxonomy navigation and browsing modes"
        >
          <TaxonomyStepper
            params={params}
            currentLevel={currentLevel}
            uid={uid}
            browseMode={browseMode}
            setBrowseMode={setBrowseMode}
          />
          {stepperScroll.canScroll && (
            <div
              className="bioindex-explore-stepper-scroll-indicator sm:hidden"
              aria-hidden="true"
            >
              <div className="bioindex-explore-stepper-scroll-track">
                <span
                  className="bioindex-explore-stepper-scroll-thumb"
                  style={{
                    width: `${stepperScroll.thumbWidth}px`,
                    transform: `translateX(${stepperScroll.thumbOffset}px)`,
                  }}
                />
              </div>
            </div>
          )}
        </section>

        {topContent}
        {browseMode === "taxonomy" ? (
          <section
            className="bioindex-explore-kingdoms"
            aria-label={`Documented ${itemName}`}
          >
            {children}
          </section>
        ) : (
          <section
            className="bioindex-explore-modes-panel"
            aria-label={currentModeTitle}
          >
            <ExplorePhotoModes
              mode={browseMode}
              dataUID={dataUID}
              routeUID={uid}
              isAdminCollectionMode={adminModeActive}
            />
          </section>
        )}
      </main>

      <footer className="bioindex-explore-footer">
        <p>© {new Date().getFullYear()} BioIndex™. All rights reserved.</p>
        <nav aria-label="Legal and support">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/community-guidelines">Guidelines</Link>
          <Link to="/copyright">Copyright</Link>
          <Link to="/contact">Contact</Link>
        </nav>
      </footer>
    </div>
  );
}
